import { useCallback, useEffect, useRef, useState } from "react";
import { setDraggingItem } from "../../../planningSlice";
import { checkPermission, getCalendarDaysDifference } from "../../../../../app/utils/helpers";
import { config } from "../../../../../app/utils/config";
import { LONG_PRESS_DELAY } from "../constants";

/**
 * Encapsulates all touch drag & drop state and handlers for the mobile scheduler.
 *
 * @param {object} options
 * @param {function} options.canEditPlanning   (teamId) => boolean
 * @param {object}   options.dynamicLeaves     leave type map from redux
 * @param {*}        options.draggingItem      redux draggingItem (sample being dragged from samples row)
 * @param {array}    options.gridData          current grid rows (read via ref to avoid stale closures)
 * @param {function} options.handleSampleDrop  (dayTs, collab, teamId) => void
 * @param {function} options.onDropCompleted   (newData, originalShift, resource) => void
 * @param {function} options.onShiftSelect     (shift) => void
 * @param {function} options.dispatch          redux dispatch
 */
export default function useSchedulerDrag({
  canEditPlanning,
  dynamicLeaves,
  draggingItem,
  gridData,
  handleSampleDrop,
  onDropCompleted,
  onShiftSelect,
  dispatch,
}) {
  // ── Drag state ──
  const [isDragging, setIsDragging] = useState(false);
  const [holdingShiftId, setHoldingShiftId] = useState(null);
  const [dragShift, setDragShift] = useState(null);
  const [, setDragTeamId] = useState(null);
  const [ghostPos, setGhostPos] = useState({ x: 0, y: 0 });
  const [dropTarget, setDropTarget] = useState(null);

  // ── Refs ──
  const dragTimerRef = useRef(null);
  const dragActiveRef = useRef(false);
  const pointerStartRef = useRef(null);
  const holdStartTimeRef = useRef(null);
  const heldShiftRef = useRef(null);
  const ghostRef = useRef(null);
  const dropTargetRef = useRef(null);
  const dragShiftRef = useRef(null);
  const dragResourceRef = useRef(null);

  // ── Stable refs for sample-drop listener ──
  const sampleDraggingRef = useRef(null);
  const gridDataRef = useRef([]);
  const handleSampleDropRef = useRef(null);

  useEffect(() => { sampleDraggingRef.current = draggingItem; }, [draggingItem]);
  useEffect(() => { gridDataRef.current = gridData; }, [gridData]);
  useEffect(() => { handleSampleDropRef.current = handleSampleDrop; }, [handleSampleDrop]);

  // ── Global pointerup listener: handles sample-to-cell drops ──
  useEffect(() => {
    const handleSamplePointerUp = (e) => {
      const currentDragging = sampleDraggingRef.current;
      if (!currentDragging || dragActiveRef.current) return;

      const el = document.elementFromPoint(e.clientX, e.clientY);
      const cell = el && el.closest("[data-day][data-collab]");
      if (cell) {
        const dayTs = parseInt(cell.getAttribute("data-day"));
        const collabId = parseInt(cell.getAttribute("data-collab"));
        const cellTeamId = parseInt(cell.getAttribute("data-team")) || null;

        for (const row of gridDataRef.current) {
          if (row.type === "collab") {
            const cId = row.collab.value || row.collab.id;
            if (cId === collabId) {
              handleSampleDropRef.current(dayTs, row.collab, cellTeamId || row.teamId);
              break;
            }
          }
        }
      }
      dispatch(setDraggingItem(null));
    };

    document.addEventListener("pointerup", handleSamplePointerUp);
    return () => document.removeEventListener("pointerup", handleSamplePointerUp);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // ── Pointer handlers ──
  const handlePointerDown = useCallback(
    (e, shift, resource, teamId) => {
      if (!canEditPlanning(teamId)) return;
      if (shift.id_sample_leave && !dynamicLeaves[shift.type]) return;
      if (e.button !== 0 && e.pointerType !== "touch") return;

      pointerStartRef.current = { x: e.clientX, y: e.clientY, shiftId: shift.id };
      holdStartTimeRef.current = Date.now();
      heldShiftRef.current = { shift, collab: resource, teamId };

      setHoldingShiftId(shift.id);
      dragTimerRef.current = setTimeout(() => {
        dragActiveRef.current = true;
        setIsDragging(true);
        setHoldingShiftId(null);
        setDragShift(shift);
        setDragTeamId(teamId);
        dragShiftRef.current = shift;
        dragResourceRef.current = resource;
        setGhostPos({ x: e.clientX - 30, y: e.clientY - 30 });
        if (navigator.vibrate) navigator.vibrate(50);
      }, LONG_PRESS_DELAY);
    },
    [canEditPlanning, dynamicLeaves]
  );

  const handlePointerMove = useCallback((e) => {
    if (dragTimerRef.current && !dragActiveRef.current) {
      if (pointerStartRef.current) {
        const dx = Math.abs(e.clientX - pointerStartRef.current.x);
        const dy = Math.abs(e.clientY - pointerStartRef.current.y);
        if (dx > 10 || dy > 10) {
          clearTimeout(dragTimerRef.current);
          dragTimerRef.current = null;
          setHoldingShiftId(null);
        }
      }
    }

    if (!dragActiveRef.current) return;

    e.preventDefault();
    setGhostPos({ x: e.clientX - 30, y: e.clientY - 30 });

    if (ghostRef.current) ghostRef.current.style.display = "none";
    const elementBelow = document.elementFromPoint(e.clientX, e.clientY);
    if (ghostRef.current) ghostRef.current.style.display = "";

    if (elementBelow) {
      const cell = elementBelow.closest("[data-day][data-collab]");
      if (cell) {
        const dayTs = parseInt(cell.getAttribute("data-day"));
        const collabId = parseInt(cell.getAttribute("data-collab"));
        const cellTeamId = parseInt(cell.getAttribute("data-team")) || null;
        const target = { dayTs, collabId, teamId: cellTeamId };
        setDropTarget(target);
        dropTargetRef.current = target;
      } else {
        setDropTarget(null);
        dropTargetRef.current = null;
      }
    }
  }, []);

  const handlePointerUp = useCallback(
    () => {
      clearTimeout(dragTimerRef.current);
      dragTimerRef.current = null;
      setHoldingShiftId(null);

      if (!dragActiveRef.current) {
        const heldFor = Date.now() - (holdStartTimeRef.current || 0);
        if (heldFor >= 200 && heldFor < LONG_PRESS_DELAY && heldShiftRef.current) {
          const { shift } = heldShiftRef.current;
          if (
            (shift.id_sample_shift != null || dynamicLeaves[shift.type] || shift.type === "AST") &&
            checkPermission(config.scopes.shifts, config.permissionTypes.edit, shift.id_team)
          ) {
            onShiftSelect(shift);
            if (navigator.vibrate) navigator.vibrate(50);
          }
        }
        holdStartTimeRef.current = null;
        heldShiftRef.current = null;
        pointerStartRef.current = null;
        return;
      }

      holdStartTimeRef.current = null;
      heldShiftRef.current = null;
      dragActiveRef.current = false;

      const currentDropTarget = dropTargetRef.current;
      const currentDragShift = dragShiftRef.current;
      const currentDragResource = dragResourceRef.current;

      if (currentDragShift && currentDropTarget) {
        const shift = currentDragShift;
        if (!currentDropTarget.collabId || currentDropTarget.collabId === shift.id_collab) {
          const fromDate = new Date(shift.from);
          const toDate = new Date(shift.to);
          const daysDiff = getCalendarDaysDifference(new Date(fromDate), new Date(toDate));
          const targetDate = new Date(currentDropTarget.dayTs);

          fromDate.setFullYear(targetDate.getFullYear());
          fromDate.setMonth(targetDate.getMonth());
          fromDate.setDate(targetDate.getDate());

          const newToDate = new Date(fromDate.getTime());
          newToDate.setDate(newToDate.getDate() + daysDiff);
          newToDate.setHours(toDate.getHours());
          newToDate.setMinutes(toDate.getMinutes());

          const data = Object.assign({}, shift, {
            from: fromDate.getTime(),
            to: newToDate.getTime(),
          });

          if (data.from !== shift.from || data.to !== shift.to) {
            onDropCompleted(data, shift, currentDragResource);
          }
        }
      }

      dropTargetRef.current = null;
      dragShiftRef.current = null;
      dragResourceRef.current = null;
      setIsDragging(false);
      setDragShift(null);
      setDragTeamId(null);
      setDropTarget(null);
      pointerStartRef.current = null;
    },
    [dynamicLeaves, onDropCompleted, onShiftSelect]
  );

  const handlePointerCancel = useCallback(() => {
    clearTimeout(dragTimerRef.current);
    dragTimerRef.current = null;
    dragActiveRef.current = false;
    setHoldingShiftId(null);
    setIsDragging(false);
    setDragShift(null);
    setDragTeamId(null);
    setDropTarget(null);
    dropTargetRef.current = null;
    dragShiftRef.current = null;
    dragResourceRef.current = null;
    pointerStartRef.current = null;
  }, []);

  // Attach move/up/cancel listeners to document when dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener("pointermove", handlePointerMove, { passive: false });
      document.addEventListener("pointerup", handlePointerUp);
      document.addEventListener("pointercancel", handlePointerCancel);
      return () => {
        document.removeEventListener("pointermove", handlePointerMove);
        document.removeEventListener("pointerup", handlePointerUp);
        document.removeEventListener("pointercancel", handlePointerCancel);
      };
    }
  }, [isDragging, handlePointerMove, handlePointerUp, handlePointerCancel]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => { clearTimeout(dragTimerRef.current); };
  }, []);

  return {
    isDragging,
    holdingShiftId,
    dragShift,
    ghostPos,
    dropTarget,
    ghostRef,
    dragActiveRef,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    handlePointerCancel,
  };
}
